import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface RecordManualPaymentParams {
  studentId: string;
  invoiceId: string;
  paymentAmount: number;
  paymentMethod: 'check' | 'cash';
  paymentDate: string;
  note?: string;
  checkNumber?: string;
  parentId: string;
}

export interface RecordManualPaymentResponse {
  status: 'success' | 'fail';
  message: string;
}

export const recordManualPayment = async ({
  ...params
}: Omit<RecordManualPaymentParams, 'parentId'>) => {
  const response = await baseAPI.post<RecordManualPaymentResponse>(
    'payment-processor/record-manual-payment',
    params,
  );
  return response.data;
};

export const useRecordManualPaymentMutation = () => {
  return useMutation({
    mutationFn: (params: RecordManualPaymentParams) => {
      const { parentId: _parentId, ...rest } = params;
      return recordManualPayment(rest);
    },
    onSuccess: (response, params) => {
      const studentId = params.studentId;
      if (response.status === 'success') {
        void queryClient.invalidateQueries({
          queryKey: [KEYS.STUDENT_SUBSCRIPTION_INVOICES, studentId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.PARENT_WALLET_TRANSACTIONS, params.parentId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.WALLET_BALANCE, params.parentId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.STUDENT_PAYMENT_HISTORY],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.PARENT_PAYMENTS_HISTORY],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.OUTSTANDING_BALANCE, studentId],
        });
        // Keep Dashboard V2 AR widgets in sync immediately after payments
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ACCOUNTS_RECEIVABLE],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
        });
      }
    },
  });
};
